import React, { useContext } from 'react'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import Divider from '@components/Divider'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  TEXT_SIZES,
  USER_ROLES,
  CONTENT_TYPES,
} from '@helpers/constants'
import Text from '@components/Text'
import ContentFilter from '@components/ContentFilter'
import SearchInput from '@components/SearchInput'
import SearchResultList from '@components/SearchResultList'
import { useSearch } from '@hooks/useSearch'
import { useFilteredItems } from '@hooks/useFilteredItems'
import {
  resetSearchFilters,
  setCategorySearchFilter,
  setLevelSearchFilter,
  setSchoolYearSearchFilter,
  setSearchTerm,
  setSearchType,
  setSubjectSearchFilter,
} from '@state/actions/searchActions'
import Loader from '@components/Loader'
import { useOwnBooksAllowed } from '@hooks/useOwnBooksAllowed'

const SearchPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    search: searchState,
    app: { userRole },
  } = state
  const ownBooksAllowed = useOwnBooksAllowed()
  const { term } = searchState
  // Teachers without own books only search books, see useOwnBooksAllowed
  const type = ownBooksAllowed ? searchState.type : CONTENT_TYPES.BOOK
  const { isLoading, searchResults } = useSearch(term, type)
  const filteredItems = useFilteredItems(searchResults, { ...searchState })

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <Text size={TEXT_SIZES.BASE} bold>
          Sök
        </Text>

        <Button
          icon={'Close'}
          size={BUTTON_SIZE.ICON_ONLY}
          handleClick={() => dispatch(closePanel(PANELS.SEARCH))}
        />
      </div>

      <Divider size={DIVIDER_SIZE.LINE} line />

      <Divider size={DIVIDER_SIZE.SMALL} />

      <SearchInput
        handleSearchTermChange={(value) => dispatch(setSearchTerm(value))}
        handleTypeChange={(value) => dispatch(setSearchType(value))}
        hideContentTypeSelect={
          userRole === USER_ROLES.STUDENT || !ownBooksAllowed
        }
      />

      <div className={'my-8'}>
        {userRole !== USER_ROLES.STUDENT &&
          searchResults &&
          searchResults.length > 0 && (
            <ContentFilter
              dark
              alignPopups={'left-[5px]'}
              filterActions={{
                setCategoryFilter: setCategorySearchFilter,
                setLevelFilter: setLevelSearchFilter,
                setSchoolYearFilter: setSchoolYearSearchFilter,
                setSubjectFilter: setSubjectSearchFilter,
                resetFilters: resetSearchFilters,
              }}
              filterState={{
                ...state.search,
                filteredItems,
              }}
            />
          )}
      </div>

      {isLoading && (
        <>
          <Divider size={DIVIDER_SIZE.BASE} />
          <Loader />
        </>
      )}

      {!isLoading && filteredItems && (
        <SearchResultList
          isStudent={userRole === USER_ROLES.STUDENT}
          results={filteredItems}
        />
      )}
      {!isLoading &&
        filteredItems.length === 0 &&
        searchResults?.length === 0 &&
        term && (
          <Text>
            {userRole !== USER_ROLES.STUDENT &&
              (ownBooksAllowed
                ? 'Inga avsnitt eller böcker matchar ditt sökord. Prova med ett annat ord!'
                : 'Inga böcker matchar ditt sökord. Prova med ett annat ord!')}
            {userRole === USER_ROLES.STUDENT &&
              'Du har ingen bok som innehåller ditt sökord. Prova med ett annat ord'}
          </Text>
        )}
    </>
  )
}

export default SearchPanel
